import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, Search, Pencil, Trash2, X, Warehouse, ExternalLink, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import MasterPartDetailModal from "./MasterPartDetailModal";
import MasterPartsBulkPanel from "./MasterPartsBulkPanel";

const empty = { sku: "", name: "", notes: "", stock: 0, min_stock: 1, warranty_months: 0, active: true };

export default function MasterPartsPanel() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [detailId, setDetailId] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const load = async (qs) => {
    if (abortRef.current) try { abortRef.current.abort(); } catch { /* noop */ }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const r = await api.get("/master-parts", { params: { q: qs ?? "" }, signal: ctrl.signal });
      setList(r.data || []);
    } catch (err) {
      if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
        console.warn("[MasterParts] load error:", err);
      }
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q.trim()), 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [q]);

  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (m) => {
    setEditing(m);
    setForm({
      sku: m.sku || "", name: m.name || "", notes: m.notes || "",
      stock: m.stock ?? 0, min_stock: m.min_stock ?? 1,
      warranty_months: m.warranty_months ?? 0,
      active: m.active !== false,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      sku: (form.sku || "").trim(),
      name: (form.name || "").trim(),
      notes: form.notes || "",
      stock: Number(form.stock) || 0,
      min_stock: Number(form.min_stock) || 0,
      warranty_months: Math.max(0, parseInt(form.warranty_months, 10) || 0),
      active: form.active !== false,
    };
    try {
      if (editing) {
        await api.put(`/master-parts/${editing.id}`, payload);
        toast.success("Artículo maestro actualizado");
      } else {
        await api.post("/master-parts", payload);
        toast.success("Artículo maestro creado");
      }
      setOpen(false);
      load(q.trim());
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (m) => {
    if (!window.confirm(`¿Eliminar "${m.name}"? Solo es posible si no hay artículos vinculados.`)) return;
    try {
      await api.delete(`/master-parts/${m.id}`);
      toast.success("Eliminado");
      load(q.trim());
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por SKU o nombre del maestro"
            data-testid="master-search"
            className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none pl-9 pr-3 py-2 text-sm"
          />
        </div>
        <button onClick={startNew} data-testid="master-new" className="ds-btn primary">
          <Plus size={15} /> Nuevo artículo maestro
        </button>
        <button onClick={() => setShowBulk((v) => !v)} data-testid="master-toggle-bulk" className={`ds-btn ${showBulk ? "primary" : ""}`}>
          <FileSpreadsheet size={15} /> Import / Export XLSX
        </button>
      </div>

      {showBulk && (
        <div className="mt-4">
          <MasterPartsBulkPanel />
        </div>
      )}

      <div className={`${showBulk ? "mt-6 pt-4 border-t border-dashed border-[#D5D7CE]" : "mt-4"} ds-card`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E0]">
              <th className="px-4 py-2.5">SKU</th>
              <th className="px-4 py-2.5">Nombre</th>
              <th className="px-4 py-2.5 text-right">Stock</th>
              <th className="px-4 py-2.5 text-right">Mín.</th>
              <th className="px-4 py-2.5">Estado</th>
              <th className="px-4 py-2.5 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((m) => {
              const low = (m.stock ?? 0) <= (m.min_stock ?? 0);
              return (
                <tr key={m.id} className="border-b border-[#E5E5E0] last:border-0 hover:bg-[#F7F7F4]" data-testid={`master-row-${m.id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{m.sku}</td>
                  <td className="px-4 py-2.5 font-medium">
                    <button
                      className="hover:text-[#6E8B3D] hover:underline flex items-center gap-1"
                      onClick={() => setDetailId(m.id)}
                      data-testid={`master-detail-${m.id}`}
                    >
                      {m.name}
                      <ExternalLink size={12} className="opacity-60" />
                    </button>
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono ${low ? "text-red-700 font-bold" : ""}`}>{m.stock ?? 0}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs">{m.min_stock ?? 0}</td>
                  <td className="px-4 py-2.5">
                    {m.active ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase border bg-[#F0F4E8] text-[#6E8B3D] border-[#6E8B3D]">Activo</span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase border bg-neutral-100 text-neutral-500 border-neutral-300">Inactivo</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => startEdit(m)} className="p-1.5 hover:bg-[#E5E5E0]" data-testid={`master-edit-${m.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => del(m)} className="p-1.5 hover:bg-red-100 text-red-700" data-testid={`master-delete-${m.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-[#525252]">
                  <Warehouse size={24} className="mx-auto mb-2 opacity-40" />
                  No hay artículos maestros. Crea el primero para compartir stock entre artículos comerciales.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-md ds-card" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
              <div className="font-display font-bold">{editing ? "Editar artículo maestro" : "Nuevo artículo maestro"}</div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#F7F7F4]"><X size={16} /></button>
            </div>
            <form onSubmit={save} className="p-4 space-y-3">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">SKU maestro *</label>
                <input
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  required
                  pattern="[A-Za-z0-9_-]+"
                  data-testid="master-form-sku"
                  className="mt-1 w-full ds-card px-3 py-2 text-sm"
                />
                <div className="mt-1 text-[10px] font-mono text-[#525252]">
                  Ej: <span className="font-semibold">BAT-A1989-A2251</span>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Nombre *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="master-form-name"
                  className="mt-1 w-full ds-card px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Stock actual</label>
                  <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="master-form-stock"
                    className="mt-1 w-full ds-card px-3 py-2 text-sm" disabled={!!editing} />
                  {editing && (
                    <div className="mt-1 text-[10px] text-[#525252]">Solo editable vía movimientos (entrada/salida de stock).</div>
                  )}
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Stock mínimo</label>
                  <input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
                    className="mt-1 w-full ds-card px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Garantía por defecto (meses)</label>
                <input type="number" min="0" value={form.warranty_months}
                  onChange={(e) => setForm({ ...form, warranty_months: e.target.value })}
                  data-testid="master-form-warranty"
                  className="mt-1 w-full ds-card px-3 py-2 text-sm" />
                <div className="mt-1 text-[10px] text-[#525252]">
                  Los artículos vinculados a este maestro heredan esta garantía en solo lectura.
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Observaciones</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                  className="mt-1 w-full ds-card px-3 py-2 text-sm" />
              </div>
              <div className="flex items-center gap-2">
                <input id="master-active" type="checkbox" checked={form.active !== false}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="h-4 w-4 accent-[#6E8B3D]" data-testid="master-form-active" />
                <label htmlFor="master-active" className="text-sm">Activo</label>
              </div>
              <button type="submit" data-testid="master-form-submit" className="w-full ds-btn primary justify-center py-2.5">
                {editing ? "Guardar" : "Crear artículo maestro"}
              </button>
            </form>
          </div>
        </div>
      )}

      {detailId && (
        <MasterPartDetailModal masterId={detailId} onClose={() => { setDetailId(""); load(q.trim()); }} />
      )}
    </div>
  );
}
